import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, CheckCircle2, ArrowLeft, Loader2, KeyRound, ShieldCheck, Camera } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PhotoCapture from "@/components/PhotoCapture";

function useLists() {
  const [lists, setLists] = useState({ faculties: [], departments: [], promotions: [] });
  useEffect(() => {
    Promise.all([
      api.get("/lists/faculties"),
      api.get("/lists/departments"),
      api.get("/lists/promotions"),
    ]).then(([f, d, p]) => setLists({ faculties: f.data, departments: d.data, promotions: p.data }));
  }, []);
  return lists;
}

function Field({ label, children, required, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
        {label}{required && <span className="text-unisic-red ml-1">*</span>}
      </label>
      <div className="mt-1" data-testid={testid}>{children}</div>
    </div>
  );
}

function FileInput({ value, onChange, kind = "projet", accept = "application/pdf", testid }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ path: data.path, filename: file.name });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <label className="flex items-center gap-3 border border-dashed border-slate-300 hover:border-unisic-gold px-4 py-4 rounded-sm cursor-pointer transition-colors text-sm text-slate-600" data-testid={testid}>
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        <span className="flex-1">{value ? value.filename : "Téléverser un fichier PDF"}</span>
        <input type="file" accept={accept} onChange={handle} className="hidden" />
      </label>
      {error && <div className="mt-1 text-xs text-unisic-red">{error}</div>}
    </div>
  );
}

export default function FormulaireProjet() {
  const { faculties, departments, promotions } = useLists();
  const [year, setYear] = useState(null);
  const [accessCode, setAccessCode] = useState("");
  const [codeStatus, setCodeStatus] = useState({ state: "idle", info: null, error: "" });
  const [form, setForm] = useState({
    group_name: "",
    group_leader: "",
    members: "",
    supervisor_name: "",
    supervisor_email: "",
    student_email: "",
    phone: "",
    faculty: "",
    department: "",
    promotion: "",
    subject: "",
    abstract: "",
    keywords: "",
    file_path: "",
    annexes_path: "",
  });
  const [pdf, setPdf] = useState(null);
  const [annexes, setAnnexes] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/academic-years/active").then((r) => setYear(r.data?.label)).catch(() => {});
    api.get("/settings").then((r) => setRequireCode(r.data?.require_student_code ?? true)).catch(() => {});
  }, []);

  const validateCode = async () => {
    if (!accessCode.trim()) return;
    setCodeStatus({ state: "checking", info: null, error: "" });
    try {
      const { data } = await api.get(`/student-codes/validate/${encodeURIComponent(accessCode.trim().toUpperCase())}`);
      setCodeStatus({ state: "valid", info: data, error: "" });
      setForm((p) => ({
        ...p,
        student_email: data.student_email || p.student_email,
        phone: data.phone || p.phone,
        faculty: data.faculty || p.faculty,
        department: data.department || p.department,
        promotion: data.promotion || p.promotion,
        group_leader: data.student_name || p.group_leader,
      }));
    } catch (err) {
      setCodeStatus({ state: "invalid", info: null, error: formatApiError(err) });
    }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const formEnabled = !requireCode || codeStatus.state === "valid";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (requireCode && codeStatus.state !== "valid") {
      setError("Veuillez d'abord valider votre code d'accès.");
      return;
    }
    if (!pdf) {
      setError("Le fichier PDF principal est obligatoire.");
      return;
    }
    if (!justifAcad || !justifDepot || !justifPlagiat) {
      setError("Les 3 photos de justificatifs sont obligatoires (frais académiques, frais de dépôt, frais anti-plagiat).");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        access_code: requireCode ? accessCode.trim().toUpperCase() : null,
        file_path: pdf.path,
        annexes_path: annexes?.path || null,
        justif_academiques: justifAcad.path,
        justif_depot: justifDepot.path,
        justif_antiplagiat: justifPlagiat.path,
      };
      const { data } = await api.post("/deposits/projet-tutore", payload);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="depot-success">
        <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="mt-6 text-3xl font-bold text-unisic-blue">Projet tutoré enregistré</h1>
        <p className="mt-3 text-slate-600">
          Un accusé de réception a été envoyé au chef de groupe et à l'encadreur.
        </p>
        <div className="mt-8 paper-card p-6 text-left">
          <div className="eyebrow eyebrow-dark">Numéro de suivi</div>
          <div className="mt-2 font-mono text-lg text-unisic-blue" data-testid="tracking-number">
            {result.tracking_number}
          </div>
        </div>
        <div className="mt-8 flex gap-4 justify-center">
          <button className="btn-secondary" onClick={() => nav("/suivi-depot")}>
            Suivre le dépôt
          </button>
          <button className="btn-primary" onClick={() => nav("/plateformes/depots")}>
            Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
      <button onClick={() => nav(-1)} className="text-sm text-slate-500 hover:text-unisic-blue flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow eyebrow-dark">Formulaire</div>
          <h1 className="mt-2 text-3xl font-bold text-unisic-blue">Dépôt d'un projet tutoré</h1>
        </div>
        {year && (
          <div className="text-xs uppercase tracking-widest bg-unisic-gold/15 text-unisic-blue px-3 py-1.5 rounded-sm">
            Année académique · {year}
          </div>
        )}
      </div>
      <span className="divider-gold mt-3" />

      {/* Access code gate */}
      {requireCode && (
      <div className="mt-8 paper-card p-6 border-l-4 border-l-unisic-gold" data-testid="code-gate">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="w-4 h-4 text-unisic-gold" />
          <div className="eyebrow eyebrow-dark">Étape 1 — Code d'accès</div>
        </div>
        <p className="text-sm text-slate-600">
          Saisissez le code d'accès qui vous a été remis par le Secrétariat Général à la Recherche.
          Sans ce code, le dépôt n'est pas autorisé.
        </p>
        <div className="mt-4 flex gap-3 flex-wrap">
          <input
            value={accessCode}
            onChange={(e) => {
              setAccessCode(e.target.value.toUpperCase());
              setCodeStatus({ state: "idle", info: null, error: "" });
            }}
            placeholder="Ex : A9K3PF"
            className="flex-1 min-w-[200px] border border-slate-300 px-4 py-3 rounded-sm text-sm font-mono uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-unisic-gold"
            disabled={codeStatus.state === "valid"}
            data-testid="input-access-code"
          />
          {codeStatus.state !== "valid" ? (
            <button
              type="button"
              onClick={validateCode}
              disabled={!accessCode.trim() || codeStatus.state === "checking"}
              className="btn-primary text-sm"
              data-testid="btn-validate-code"
            >
              {codeStatus.state === "checking" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Valider le code
            </button>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 text-green-700 rounded-sm text-sm" data-testid="code-valid-badge">
              <CheckCircle2 className="w-4 h-4" /> Code validé
            </div>
          )}
        </div>
        {codeStatus.state === "invalid" && (
          <div className="mt-3 text-sm text-unisic-red" data-testid="code-error">{codeStatus.error}</div>
        )}
      </div>
      )}

      <fieldset disabled={!formEnabled} className={formEnabled ? "" : "opacity-40 pointer-events-none"}>
      <form onSubmit={submit} className="mt-10 grid gap-6 md:grid-cols-2" data-testid="form-projet">
        <Field label="Nom du groupe" required>
          <input required value={form.group_name} onChange={(e) => set("group_name", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        </Field>
        <Field label="Chef du groupe" required>
          <input required value={form.group_leader} onChange={(e) => set("group_leader", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Liste des membres (un par ligne)" required>
            <textarea required rows={4} value={form.members} onChange={(e) => set("members", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
          </Field>
        </div>
        <Field label="E-mail de contact du groupe" required>
          <input required type="email" value={form.student_email} onChange={(e) => set("student_email", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        </Field>
        <Field label="Téléphone" required>
          <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        </Field>
        <Field label="Faculté" required>
          <select required value={form.faculty} onChange={(e) => set("faculty", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white">
            <option value="">Sélectionner...</option>
            {faculties.map((f) => <option key={f.id} value={f.name}>{f.name}</option>)}
          </select>
        </Field>
        <Field label="Département" required>
          <select required value={form.department} onChange={(e) => set("department", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white">
            <option value="">Sélectionner...</option>
            {departments.map((f) => <option key={f.id} value={f.name}>{f.name}</option>)}
          </select>
        </Field>
        <Field label="Promotion" required>
          <select required value={form.promotion} onChange={(e) => set("promotion", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white">
            <option value="">Sélectionner...</option>
            {promotions.map((f) => <option key={f.id} value={f.name}>{f.name}</option>)}
          </select>
        </Field>
        <Field label="Sujet du projet" required>
          <input required value={form.subject} onChange={(e) => set("subject", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        </Field>
        <Field label="Nom de l'encadreur" required>
          <input required value={form.supervisor_name} onChange={(e) => set("supervisor_name", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        </Field>
        <Field label="E-mail de l'encadreur" required>
          <input required type="email" value={form.supervisor_email} onChange={(e) => set("supervisor_email", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        </Field>

        <div className="md:col-span-2">
          <Field label="Résumé" required>
            <textarea required rows={5} value={form.abstract} onChange={(e) => set("abstract", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
          </Field>
        </div>
        <Field label="Mots-clés" required>
          <input required value={form.keywords} onChange={(e) => set("keywords", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
        </Field>
        <div />

        <div className="md:col-span-2 grid md:grid-cols-2 gap-6">
          <Field label="Rapport PDF principal" required>
            <FileInput value={pdf} onChange={setPdf} kind="projet" testid="file-pdf" />
          </Field>
          <Field label="Annexes (facultatif)">
            <FileInput value={annexes} onChange={setAnnexes} kind="annexes" accept="application/pdf,application/zip" testid="file-annexes" />
          </Field>
        </div>

        {/* JUSTIFICATIFS PHOTO — 3 obligatoires */}
        <div className="md:col-span-2 mt-4">
          <div className="flex items-center gap-2 mb-4">
            <Camera className="w-5 h-5 text-unisic-gold" />
            <div className="eyebrow eyebrow-dark">Justificatifs par photo — obligatoires</div>
          </div>
          <p className="text-xs text-slate-500 mb-4 max-w-2xl">
            Ouvrez votre appareil photo et capturez directement les 3 pièces justificatives ci-dessous.
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <PhotoCapture label="Frais académiques" value={justifAcad} onChange={setJustifAcad} kind="justif-academiques" testid="photo-academiques" />
            <PhotoCapture label="Frais de dépôt" value={justifDepot} onChange={setJustifDepot} kind="justif-depot" testid="photo-depot" />
            <PhotoCapture label="Frais anti-plagiat" value={justifPlagiat} onChange={setJustifPlagiat} kind="justif-antiplagiat" testid="photo-antiplagiat" />
          </div>
        </div>

        {error && (
          <div className="md:col-span-2 text-sm text-unisic-red bg-red-50 border border-red-100 px-4 py-3 rounded-sm" data-testid="form-error">
            {error}
          </div>
        )}

        <div className="md:col-span-2 flex justify-end pt-4">
          <button type="submit" disabled={submitting || !formEnabled} className="btn-primary" data-testid="btn-submit-projet">
            {submitting ? "Envoi en cours..." : "Soumettre le projet"}
          </button>
        </div>
      </form>
      </fieldset>
    </div>
  );
}
